/**
 * Merchant-surface analytics for the embedded admin.
 *
 * Different constraints from the storefront, so a different design:
 *
 *   - the merchant is a known, identified party (their shop domain IS the
 *     distinct id), so there is no consent gate here — this is first-party
 *     product usage of a tool they installed;
 *   - but the admin is full of things that must never be recorded: the App
 *     Bridge session token in the URL, the `host` parameter, a size guide's
 *     contents, and the merchant's own email in Shopify's chrome. So masking is
 *     stricter, not looser, than on the storefront;
 *   - persistence stays memory-only anyway. The admin runs in an iframe inside
 *     Shopify, and leaving identifiers in a merchant's browser buys us nothing.
 *
 * The SDK is loaded at runtime from our own ingest proxy rather than bundled,
 * for the same reason as the storefront: an empty browser key must mean *no
 * network request at all*, which is what makes "clear the key" a real kill
 * switch rather than a partial one.
 */
import { sanitizeBrowserEvent } from './browserSanitize';

export interface MerchantAnalyticsConfig {
  key: string;
  host: string;
  environment: string;
  shop: string;
  appVersion: string;
  /** Session replay for merchant surfaces. Off unless explicitly enabled. */
  replayEnabled: boolean;
}

interface PostHogLike {
  init: (key: string, options: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  identify?: (id: string, properties?: Record<string, unknown>) => void;
  register?: (properties: Record<string, unknown>) => void;
  startSessionRecording?: () => void;
  stopSessionRecording?: () => void;
  __loaded?: boolean;
}

/** posthog-js's library object, whose `init` can mint a NAMED instance. */
interface PostHogLibrary extends PostHogLike {
  init: (key: string, options: Record<string, unknown>, name?: string) => PostHogLike | undefined;
  [instanceName: string]: unknown;
}

/**
 * The name of the instance Aisthetix owns.
 *
 * Another app's embedded admin page, or Shopify's own chrome, can already have
 * put a PostHog instance on `window`. Reusing it — which this loader used to do
 * whenever `__loaded` was set — would identify the merchant into somebody
 * else's project and start a recorder configured with none of our masks and
 * none of our `before_send`. posthog-js supports naming an instance, so ours is
 * named and nothing else is ever touched.
 */
export const AISTHETIX_INSTANCE = 'aisthetixAdmin';

/** The library object, if any script has loaded it. */
function posthogLibrary(): PostHogLibrary | undefined {
  return (window as unknown as { posthog?: PostHogLibrary }).posthog;
}

/**
 * OUR instance, held explicitly rather than rediscovered from the global — the
 * global can be replaced by a later script.
 */
let instance: PostHogLike | null = null;

let ready: Promise<PostHogLike | null> | null = null;
let config: MerchantAnalyticsConfig | null = null;

/** The class every sensitive region in the admin carries. */
export const NO_CAPTURE_CLASS = 'ph-no-capture';

/**
 * Load and initialise, once.
 *
 * @param next - Configuration, from the backend
 * @returns The SDK, or null when analytics are off or unreachable
 */
export function initMerchantAnalytics(next: MerchantAnalyticsConfig): Promise<PostHogLike | null> {
  config = next;

  // An empty key is the kill switch: nothing is requested, nothing initialises.
  if (!next.key || !next.host) return Promise.resolve(null);
  if (ready) return ready;

  ready = new Promise<PostHogLike | null>((resolve) => {
    try {
      // Already loaded — by another app, or by us. Either way the LIBRARY is
      // here and only our INSTANCE is missing, so there is nothing to fetch.
      const present = posthogLibrary();
      if (present?.__loaded) {
        resolve(initAisthetixInstance(present, next));
        return;
      }

      const script = document.createElement('script');
      script.src = `${next.host.replace(/\/$/, '')}/static/array.js`;
      script.async = true;
      script.onload = () => {
        const library = posthogLibrary();
        if (!library) {
          resolve(null);
          return;
        }
        resolve(initAisthetixInstance(library, next));
      };
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    } catch {
      resolve(null);
    }
  });

  return ready;
}

/**
 * Create (or retrieve) the instance the admin records through.
 *
 * Never the default one — see AISTHETIX_INSTANCE.
 *
 * @param library - posthog-js, however it got here
 * @param next - Configuration, from the backend
 * @returns Our instance, or null when it could not be created
 */
function initAisthetixInstance(
  library: PostHogLibrary,
  next: MerchantAnalyticsConfig
): PostHogLike | null {
  try {
    const created = library.init(next.key, {
        api_host: next.host,
        persistence: 'memory',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        // The only hook that sees every outgoing event, $snapshot included.
        // This surface runs inside Shopify's admin iframe, so its own
        // address carries `host`, `id_token` and `session`, and posthog-js
        // attaches that address to captures, identify and replay traffic
        // alike — none of which passes through the server sanitizer. See
        // browserSanitize.ts.
        before_send: sanitizeBrowserEvent,
        disable_surveys: true,
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          // The admin's own sensitive regions, marked in the components.
          // A merchant's size guide, their plan's billing URL and Shopify's
          // account chrome all live in this iframe.
          maskTextSelector: '[data-ph-mask]',
          blockSelector: `.${NO_CAPTURE_CLASS}, img[src^="data:"], canvas`,
          collectFonts: false,
          recordCrossOriginIframes: false,
        },
      },
      // The name is the whole point. Without it this is `init` on the DEFAULT
      // instance, which in an embedded admin may belong to another app.
      AISTHETIX_INSTANCE
    );
    // `init` returns the named instance; the property lookup is the documented
    // fallback for builds that return void.
    const named = created ?? (library[AISTHETIX_INSTANCE] as PostHogLike | undefined);
    if (!named) return null;

    // The shop domain is the merchant's identity everywhere else in this
    // product, so it is the distinct id here too. Never an email: an email
    // would make the person profile a contact record.
    named.identify?.(next.shop);
    named.register?.({
      store: next.shop,
      platform: 'shopify',
      surface: 'merchant_admin',
      environment: next.environment,
      appVersion: next.appVersion,
      schemaVersion: '1.0.0',
    });
    if (next.replayEnabled) named.startSessionRecording?.();
    instance = named;
    return named;
  } catch {
    return null;
  }
}

/**
 * Record a merchant event.
 *
 * Fire-and-forget by design: nothing in the admin waits on analytics, and a
 * caller that had to await this would be a caller that could be blocked by it.
 *
 * @param event - A contract event name
 * @param properties - Event properties
 */
export function captureMerchantEvent(event: string, properties: Record<string, unknown> = {}): void {
  if (!config?.key) return;
  void ready
    ?.then((loaded) => {
      try {
        loaded?.capture?.(event, properties);
      } catch {
        // Analytics must never break the admin.
      }
    })
    .catch(() => undefined);
}

/** Stop recording. Used when the merchant leaves a surface we record. */
export function stopMerchantReplay(): void {
  try {
    // OUR instance, held explicitly. Stopping whatever happens to be on
    // `window.posthog` would switch off another app's recording.
    instance?.stopSessionRecording?.();
  } catch {
    // Nothing to do.
  }
}

/** Reset module state. Tests only. */
export function resetMerchantAnalyticsForTests(): void {
  ready = null;
  instance = null;
  config = null;
}
